// Authentication Service
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  User,
  updateProfile,
  reauthenticateWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult
} from 'firebase/auth'
import { doc, setDoc, getDoc, collection, query, where, getDocs, deleteDoc, updateDoc } from 'firebase/firestore'
import { auth, db, COLLECTIONS, getSecondaryAuth, cleanupSecondaryAuth, isFirebaseConfigured } from './firebase'


export type UserRole =
  | 'super_admin'
  | 'org_admin'
  | 'manager'
  | 'staff'
  | 'customer'
  | 'admin'
  | 'cashier'
  | 'sales'

export interface UserData {
  uid: string
  email: string
  displayName: string
  firstName?: string
  companyName: string
  companyId?: string
  businessId?: string
  phone?: string
  role: UserRole
  status: 'active' | 'inactive'
  createdAt: string
  lastLogin: string
  createdBy?: string // UID of admin who created this user
}

interface DemoStoredUser extends UserData {
  password: string
}

const DEMO_USERS_STORAGE_KEY = 'anna_demo_users'
const DEMO_AUTH_EVENT = 'anna-demo-auth-changed'
const DEMO_ADMIN_EMAIL = 'demo@anna.local'
const DEMO_ADMIN_PASSWORD = 'demo123'
const DEMO_STAFF_EMAIL = 'staff@anna.local'
const DEMO_STAFF_PASSWORD = 'staff123'

// Derive a stable companyId from company name or email (fallback to full email)
// This avoids collisions for users on common domains (e.g., gmail.com)
const deriveCompanyId = (email?: string | null, companyName?: string) => {
  const trimmedCompany = companyName?.trim()
  let source = trimmedCompany && trimmedCompany.length > 0 ? trimmedCompany : ''

  if (!source && email) {
    const [localPart, domain] = email.split('@')
    source = localPart && domain ? `${localPart}-${domain}` : email
  }

  return source
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '') || 'default-company'
}

const isDemoMode = () => !isFirebaseConfigured()

const readDemoUsers = (): DemoStoredUser[] => {
  if (typeof window === 'undefined') return []

  try {
    const raw = window.localStorage.getItem(DEMO_USERS_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed as DemoStoredUser[] : []
  } catch (error) {
    console.error('Failed to read demo users:', error)
    return []
  }
}

const saveDemoUsers = (users: DemoStoredUser[]) => {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(DEMO_USERS_STORAGE_KEY, JSON.stringify(users))
}

const toSessionUser = (user: DemoStoredUser): UserData => {
  const { password: _password, ...sessionUser } = user
  return sessionUser
}

const emitDemoAuthChange = () => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(DEMO_AUTH_EVENT))
}

const getCurrentDemoSessionUser = (): UserData | null => {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.localStorage.getItem('user')
    return raw ? JSON.parse(raw) as UserData : null
  } catch (error) {
    console.error('Failed to read demo session:', error)
    return null
  }
}

const persistDemoSession = (user: DemoStoredUser | UserData) => {
  if (typeof window === 'undefined') return

  const sessionUser = 'password' in user ? toSessionUser(user) : user
  window.localStorage.setItem('user', JSON.stringify(sessionUser))
  emitDemoAuthChange()
}

const clearDemoSession = () => {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem('user')
  emitDemoAuthChange()
}

const migrateDemoCompanyLocalStorage = (fromCompanyId?: string, toCompanyId?: string) => {
  if (typeof window === 'undefined' || !fromCompanyId || !toCompanyId || fromCompanyId === toCompanyId) return

  const companyScopedKeys = ['thisai_crm_items']

  companyScopedKeys.forEach(baseKey => {
    const fromKey = `${baseKey}_${fromCompanyId}`
    const toKey = `${baseKey}_${toCompanyId}`
    const fromRaw = window.localStorage.getItem(fromKey)
    if (!fromRaw) return

    try {
      const fromItems = JSON.parse(fromRaw)
      const toItems = JSON.parse(window.localStorage.getItem(toKey) || '[]')
      if (!Array.isArray(fromItems) || !Array.isArray(toItems)) return

      const merged = new Map<string, any>()
      toItems.forEach(item => {
        if (item?.id) merged.set(item.id, item)
      })
      fromItems.forEach(item => {
        if (!item?.id) return
        merged.set(item.id, { ...item, companyId: toCompanyId })
      })

      window.localStorage.setItem(toKey, JSON.stringify(Array.from(merged.values())))
    } catch (error) {
      console.warn('Failed to migrate demo company data:', error)
    }
  })
}

const ensureDemoUsers = (): DemoStoredUser[] => {
  const existingUsers = readDemoUsers()

  const now = new Date().toISOString()
  const seededUsers: DemoStoredUser[] = [
    {
      uid: 'local_demo_admin',
      email: DEMO_ADMIN_EMAIL,
      displayName: 'Demo Admin',
      companyName: 'Demo Company',
      companyId: deriveCompanyId(DEMO_ADMIN_EMAIL, 'Demo Company'),
      role: 'org_admin',
      status: 'active',
      createdAt: now,
      lastLogin: now,
      password: DEMO_ADMIN_PASSWORD
    },
    {
      uid: 'local_demo_staff',
      email: DEMO_STAFF_EMAIL,
      displayName: 'Demo Staff',
      companyName: 'Demo Company',
      companyId: deriveCompanyId(DEMO_ADMIN_EMAIL, 'Demo Company'),
      role: 'staff',
      status: 'active',
      createdAt: now,
      lastLogin: now,
      password: DEMO_STAFF_PASSWORD
    }
  ]

  const mergedUsers = [...existingUsers]
  seededUsers.forEach(seedUser => {
    const existingIndex = mergedUsers.findIndex(user => user.email.toLowerCase() === seedUser.email.toLowerCase())
    if (existingIndex >= 0) {
      migrateDemoCompanyLocalStorage(mergedUsers[existingIndex].companyId, seedUser.companyId)
      mergedUsers[existingIndex] = {
        ...mergedUsers[existingIndex],
        uid: seedUser.uid,
        companyName: seedUser.companyName,
        companyId: seedUser.companyId,
        role: seedUser.role,
        status: 'active'
      }
    } else {
      mergedUsers.push(seedUser)
    }
  })

  saveDemoUsers(mergedUsers)
  return mergedUsers
}

const signInDemo = async (email: string, password: string): Promise<UserData> => {
  const normalizedEmail = email.trim().toLowerCase()
  const users = ensureDemoUsers()
  const existingUser = users.find(user => user.email.toLowerCase() === normalizedEmail)

  if (!existingUser) {
    throw new Error('No account found with this email')
  }

  if (existingUser.password !== password) {
    throw new Error('Incorrect password')
  }

  const updatedUser: DemoStoredUser = {
    ...existingUser,
    lastLogin: new Date().toISOString()
  }

  saveDemoUsers(users.map(user => user.uid === updatedUser.uid ? updatedUser : user))
  persistDemoSession(updatedUser)
  return toSessionUser(updatedUser)
}

const createDemoAccount = async (
  email: string,
  password: string,
  companyName: string,
  displayName: string,
  role: UserRole
): Promise<UserData> => {
  const normalizedEmail = email.trim().toLowerCase()
  const users = ensureDemoUsers()

  if (users.some(user => user.email.toLowerCase() === normalizedEmail)) {
    throw new Error('An account with this email already exists')
  }

  const now = new Date().toISOString()
  const newUser: DemoStoredUser = {
    uid: `local_${Date.now()}`,
    email: normalizedEmail,
    displayName: displayName.trim(),
    companyName: companyName.trim(),
    companyId: deriveCompanyId(normalizedEmail, companyName),
    role,
    status: 'active',
    createdAt: now,
    lastLogin: now,
    password
  }

  saveDemoUsers([...users, newUser])
  persistDemoSession(newUser)
  return toSessionUser(newUser)
}

const reauthenticateDemo = async (password: string): Promise<void> => {
  const sessionUser = getCurrentDemoSessionUser()
  if (!sessionUser?.email) {
    throw new Error('No user is currently signed in')
  }

  const existingUser = ensureDemoUsers().find(user => user.email.toLowerCase() === sessionUser.email.toLowerCase())
  if (!existingUser || existingUser.password !== password) {
    throw new Error('Incorrect password')
  }
}

const updateDemoProfile = async (updates: { displayName?: string }): Promise<Partial<UserData>> => {
  const sessionUser = getCurrentDemoSessionUser()
  if (!sessionUser) {
    throw new Error('No user is currently signed in')
  }

  const nextDisplayName = (updates.displayName || '').trim()
  const changedFields: Partial<UserData> = {}

  if (nextDisplayName && nextDisplayName !== sessionUser.displayName) {
    changedFields.displayName = nextDisplayName
  }

  if (Object.keys(changedFields).length === 0) {
    return {}
  }

  const users = ensureDemoUsers()
  let nextSessionUser: UserData = { ...sessionUser, ...changedFields }

  const updatedUsers = users.map(user => {
    if (user.uid !== sessionUser.uid) return user
    const nextUser = { ...user, ...changedFields }
    nextSessionUser = toSessionUser(nextUser)
    return nextUser
  })

  saveDemoUsers(updatedUsers)
  persistDemoSession(nextSessionUser)
  return changedFields
}

// Rate limiting for sign-in attempts
const signInAttempts = new Map<string, { count: number; lastAttempt: number }>()
const MAX_ATTEMPTS = 5
const LOCKOUT_DURATION = 15 * 60 * 1000 // 15 minutes

const checkRateLimit = (email: string): void => {
  const now = Date.now()
  const attempts = signInAttempts.get(email)

  if (attempts) {
    // Reset counter if lockout period has passed
    if (now - attempts.lastAttempt > LOCKOUT_DURATION) {
      signInAttempts.delete(email)
      return
    }

    if (attempts.count >= MAX_ATTEMPTS) {
      const remainingTime = Math.ceil((LOCKOUT_DURATION - (now - attempts.lastAttempt)) / 60000)
      throw new Error(`Too many failed attempts. Please try again in ${remainingTime} minutes.`)
    }
  }
}

const recordFailedAttempt = (email: string): void => {
  const now = Date.now()
  const attempts = signInAttempts.get(email) || { count: 0, lastAttempt: 0 }
  attempts.count += 1
  attempts.lastAttempt = now
  signInAttempts.set(email, attempts)
}

const recordSuccessfulAttempt = (email: string): void => {
  signInAttempts.delete(email)
}

// Sign in with email and password
export const signIn = async (email: string, password: string): Promise<UserData> => {
  if (!auth) {
    if (isDemoMode()) {
      return signInDemo(email, password)
    }
    throw new Error('Firebase authentication not initialized')
  }

  // SECURITY: Check rate limiting
  checkRateLimit(email)

  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password)
    // SECURITY: Clear failed attempts on successful login
    recordSuccessfulAttempt(email)
    const user = userCredential.user

    // Update last login
    if (db) {
      const userDocRef = doc(db, COLLECTIONS.USERS, user.uid)
      const userDoc = await getDoc(userDocRef)
      const now = new Date().toISOString()

      if (!userDoc.exists()) {
        await firebaseSignOut(auth).catch(() => {})
        throw new Error('Your login exists, but no company access profile was found. Ask the admin to create users/' + user.uid + ' with the correct companyId.')
      }

      const existingData = userDoc.data() as UserData
      const mappedCompanyId = existingData.companyId || (existingData as any).businessId || null
      if (!mappedCompanyId) {
        await firebaseSignOut(auth).catch(() => {})
        throw new Error('Your account is not mapped to a company. Ask the admin to set users/' + user.uid + '.companyId or businessId before using POS.')
      }

      const mergedData: UserData = {
        ...existingData,
        uid: existingData.uid || user.uid,
        email: existingData.email || user.email || '',
        displayName: existingData.displayName || user.displayName || '',
        companyId: mappedCompanyId,
        lastLogin: now
      }

      await setDoc(userDocRef, { lastLogin: now }, { merge: true })

      return mergedData
    }

    return {
      uid: user.uid,
      email: user.email || '',
      displayName: user.displayName || '',
      companyName: '',
      role: 'staff',
      status: 'active',
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString()
    }
  } catch (error) {
    console.error('Sign in error:', error)

    // SECURITY: Record failed attempt for rate limiting
    if (email) {
      recordFailedAttempt(email)
    }

    if (error && typeof error === 'object' && 'code' in error) {
        throw new Error(getAuthErrorMessage((error as {code: string}).code))
    }
    if (error instanceof Error) {
      throw error
    }
    throw new Error('An unknown sign-in error occurred.');
  }
}

// ============ SOCIAL LOGIN FUNCTIONS ============

// Sign in with Google
export const signInWithGoogle = async (): Promise<UserData> => {
  if (!auth) {
    if (isDemoMode()) {
      throw new Error('Google sign-in requires Firebase setup in .env.local')
    }
    throw new Error('Firebase authentication not initialized')
  }

  try {
    const provider = new GoogleAuthProvider()
    provider.addScope('email')
    provider.addScope('profile')

    const result = await signInWithPopup(auth, provider)
    const user = result.user

    return await handleSocialLoginUser(user, 'google')
  } catch (error) {
    console.error('Google sign in error:', error)
    if (error && typeof error === 'object' && 'code' in error) {
        throw new Error(getSocialAuthErrorMessage((error as {code: string}).code))
    }
    throw new Error('An unknown Google sign-in error occurred.');
  }
}

// Sign in with Facebook
export const signInWithFacebook = async (): Promise<UserData> => {
  if (!auth) {
    if (isDemoMode()) {
      throw new Error('Facebook sign-in requires Firebase setup in .env.local')
    }
    throw new Error('Firebase authentication not initialized')
  }

  try {
    const provider = new FacebookAuthProvider()
    provider.addScope('email')
    provider.addScope('public_profile')

    const result = await signInWithPopup(auth, provider)
    const user = result.user

    return await handleSocialLoginUser(user, 'facebook')
  } catch (error) {
    console.error('Facebook sign in error:', error)
    if (error && typeof error === 'object' && 'code' in error) {
        throw new Error(getSocialAuthErrorMessage((error as {code: string}).code))
    }
    throw new Error('An unknown Facebook sign-in error occurred.');
  }
}

// Sign in with Apple
export const signInWithApple = async (): Promise<UserData> => {
  if (!auth) {
    if (isDemoMode()) {
      throw new Error('Apple sign-in requires Firebase setup in .env.local')
    }
    throw new Error('Firebase authentication not initialized')
  }

  try {
    const provider = new OAuthProvider('apple.com')
    provider.addScope('email')
    provider.addScope('name')

    const result = await signInWithPopup(auth, provider)
    const user = result.user

    return await handleSocialLoginUser(user, 'apple')
  } catch (error) {
    console.error('Apple sign in error:', error)
    if (error && typeof error === 'object' && 'code' in error) {
        throw new Error(getSocialAuthErrorMessage((error as {code: string}).code))
    }
    throw new Error('An unknown Apple sign-in error occurred.');
  }
}

// Handle social login user - create or update user document
const handleSocialLoginUser = async (user: User, provider: string): Promise<UserData> => {
  const now = new Date().toISOString()

  if (db) {
    const userDocRef = doc(db, COLLECTIONS.USERS, user.uid)
    const userDoc = await getDoc(userDocRef)

    if (userDoc.exists()) {
      // Existing user - update last login
      const existingData = userDoc.data() as UserData
      const mappedCompanyId = existingData.companyId || (existingData as any).businessId || null
      if (!mappedCompanyId) {
        throw new Error('Your account is not mapped to a company. Ask the admin to set users/' + user.uid + '.companyId or businessId before using POS.')
      }
      const updatedData: UserData = {
        ...existingData,
        companyId: mappedCompanyId,
        lastLogin: now
      }
      await setDoc(userDocRef, { lastLogin: now }, { merge: true })
      return updatedData
    } else {
      // New user - create document with restricted role
      const companyId = deriveCompanyId(user.email, user.displayName || '')
      const newUserData: UserData = {
        uid: user.uid,
        email: user.email || '',
        displayName: user.displayName || user.email?.split('@')[0] || 'User',
        companyName: '', // User will need to set this in settings
        companyId,
      role: 'staff', // SECURITY: Start with lowest privilege role
        status: 'active',
        createdAt: now,
        lastLogin: now
      }
      await setDoc(userDocRef, newUserData)
      return newUserData
    }
  }

  // Fallback if db is not available
  return {
    uid: user.uid,
    email: user.email || '',
    displayName: user.displayName || '',
    companyName: '',
    role: 'staff', // SECURITY: Start with lowest privilege role
    status: 'active',
    createdAt: now,
    lastLogin: now
  }
}

// Helper function for social auth error messages
const getSocialAuthErrorMessage = (errorCode: string): string => {
  switch (errorCode) {
    case 'auth/popup-closed-by-user':
      return 'Sign-in popup was closed. Please try again.'
    case 'auth/popup-blocked':
      return 'Popup was blocked by browser. Please allow popups and try again.'
    case 'auth/cancelled-popup-request':
      return 'Sign-in cancelled. Please try again.'
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email using a different sign-in method.'
    case 'auth/auth-domain-config-required':
      return 'Authentication domain not configured. Please contact support.'
    case 'auth/operation-not-allowed':
      return 'This sign-in method is not enabled. Please contact support.'
    case 'auth/unauthorized-domain':
      return 'This domain is not authorized for sign-in. Please contact support.'
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection.'
    default:
      return 'Sign-in failed. Please try again.'
  }
}

// Sign out
export const signOut = async (): Promise<void> => {
  if (!auth) {
    if (isDemoMode()) {
      clearDemoSession()
      return
    }
    throw new Error('Firebase authentication not initialized')
  }

  try {
    await firebaseSignOut(auth)
  } catch (error: unknown) {
    console.error('Sign out error:', error)
    throw error
  }
}

// Create admin account (for initial setup)
export const createAdminAccount = async (
  email: string,
  password: string,
  companyName: string,
  displayName: string
): Promise<UserData> => {
  if (!auth || !db) {
    if (isDemoMode()) {
      return createDemoAccount(email, password, companyName, displayName, 'org_admin')
    }
    throw new Error('Firebase not initialized')
  }

  try {
    // Create user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password)
    const user = userCredential.user

    // Update profile
    await updateProfile(user, {
      displayName: displayName
    })

    // Create user document
    const userData: UserData = {
      uid: user.uid,
      email: user.email || '',
      displayName: displayName,
      companyName: companyName,
      companyId: deriveCompanyId(user.email, companyName),
      role: 'org_admin',
      status: 'active',
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString()
    }

    const userDocRef = doc(db, COLLECTIONS.USERS, user.uid)
    await setDoc(userDocRef, userData)

    return userData
  } catch (error) {
    console.error('Create account error:', error)
    if (error && typeof error === 'object' && 'code' in error) {
        throw new Error(getAuthErrorMessage((error as {code: string}).code))
    }
    throw new Error('An unknown error occurred while creating the account.');
  }
}

// Create super admin account (for owner setup)
export const createSuperAdminAccount = async (
  email: string,
  password: string,
  companyName: string,
  displayName: string
): Promise<UserData> => {
  if (!auth || !db) {
    if (isDemoMode()) {
      return createDemoAccount(email, password, companyName, displayName, 'super_admin')
    }
    throw new Error('Firebase not initialized')
  }

  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password)
    const user = userCredential.user

    await updateProfile(user, {
      displayName: displayName
    })

    const userData: UserData = {
      uid: user.uid,
      email: user.email || '',
      displayName: displayName,
      companyName: companyName,
      companyId: deriveCompanyId(user.email, companyName),
      role: 'super_admin',
      status: 'active',
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString()
    }

    const userDocRef = doc(db, COLLECTIONS.USERS, user.uid)
    await setDoc(userDocRef, userData)

    return userData
  } catch (error) {
    console.error('Create super admin error:', error)
    if (error && typeof error === 'object' && 'code' in error) {
      throw new Error(getAuthErrorMessage((error as { code: string }).code))
    }
    throw new Error('An unknown error occurred while creating the account.')
  }
}

// Get current user
export const getCurrentUser = (): User | null => {
    if (!auth) {
    if (isDemoMode()) {
      return getCurrentDemoSessionUser() as unknown as User | null
    }
    return null
  }
  return auth.currentUser
}

// Re-authenticate user with email and password
// This is required for sensitive operations like password change, account deletion, etc.
export const reauthenticate = async (password: string): Promise<void> => {
  if (!auth) {
    if (isDemoMode()) {
      return reauthenticateDemo(password)
    }
    throw new Error('Firebase authentication not initialized')
  }

  const user = auth.currentUser
  if (!user || !user.email) {
    throw new Error('No user is currently signed in')
  }

  try {
    // Create credential with email and password
    const credential = EmailAuthProvider.credential(user.email, password)
    
    // Re-authenticate the user
    await reauthenticateWithCredential(user, credential)
  } catch (error) {
    console.error('Re-authentication error:', error)
    if (error && typeof error === 'object' && 'code' in error) {
        throw new Error(getAuthErrorMessage((error as {code: string}).code))
    }
    throw new Error('An unknown re-authentication error occurred.');
  }
}

// Update current user's profile (display name only)
export const updateUserProfile = async (updates: { displayName?: string }): Promise<Partial<UserData>> => {
  if (!auth) {
    if (isDemoMode()) {
      return updateDemoProfile(updates)
    }
    throw new Error('Firebase authentication not initialized')
  }

  const user = auth.currentUser
  if (!user) {
    throw new Error('No user is currently signed in')
  }

  const nextDisplayName = (updates.displayName || '').trim()
  const updateData: Partial<UserData> = {}

  if (nextDisplayName && nextDisplayName !== (user.displayName || '')) {
    await updateProfile(user, { displayName: nextDisplayName })
    updateData.displayName = nextDisplayName
  }

  if (db && Object.keys(updateData).length > 0) {
    const userDocRef = doc(db, COLLECTIONS.USERS, user.uid)
    await setDoc(
      userDocRef,
      { ...updateData, updatedAt: new Date().toISOString() },
      { merge: true }
    )
  }

  return updateData
}

// Listen to auth state changes
export const onAuthChange = (callback: (user: User | null) => void) => {
  if (!auth) {
    if (isDemoMode()) {
      const notify = () => {
        callback(getCurrentDemoSessionUser() as unknown as User | null)
      }

      notify()

      if (typeof window === 'undefined') {
        return () => {}
      }

      const handleChange = () => notify()
      window.addEventListener(DEMO_AUTH_EVENT, handleChange)
      window.addEventListener('storage', handleChange)

      return () => {
        window.removeEventListener(DEMO_AUTH_EVENT, handleChange)
        window.removeEventListener('storage', handleChange)
      }
    }

    callback(null)
    return () => {}
  }

  return onAuthStateChanged(auth, callback)
}

// Get user data from Firestore
export const getUserData = async (uid: string): Promise<UserData | null> => {
  if (!db) {
    if (isDemoMode()) {
      const existingUser = ensureDemoUsers().find(user => user.uid === uid)
      return existingUser ? toSessionUser(existingUser) : null
    }
    return null
  }

  try {
    const userDocRef = doc(db, COLLECTIONS.USERS, uid)
    const userDoc = await getDoc(userDocRef)

    if (userDoc.exists()) {
      const data = userDoc.data() as UserData
      const mappedCompanyId = data.companyId || (data as any).businessId || null
      return mappedCompanyId ? { ...data, companyId: mappedCompanyId } : data
    }

    return null
  } catch (error: unknown) {
    console.error('Get user data error:', error)
    return null
  }
}

// Helper function to convert Firebase error codes to user-friendly messages
const getAuthErrorMessage = (errorCode: string): string => {
  switch (errorCode) {
    case 'auth/invalid-email':
      return 'Invalid email address'
    case 'auth/user-disabled':
      return 'This account has been disabled'
    case 'auth/user-not-found':
      return 'No account found with this email'
    case 'auth/wrong-password':
      return 'Incorrect password'
    case 'auth/email-already-in-use':
      return 'An account with this email already exists'
    case 'auth/weak-password':
      return 'Password should be at least 6 characters'
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later'
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection'
    default:
      return 'Authentication failed. Please try again'
  }
}

// ============ STAFF MANAGEMENT FUNCTIONS ============

// Create a staff user (Manager or Staff) - Admin only
// Uses secondary Firebase app to avoid logging out the admin
export const createStaffUser = async (
  email: string,
  password: string,
  displayName: string,
  role: 'manager' | 'staff',
  adminData: UserData
): Promise<UserData> => {
  if (!auth || !db) {
    throw new Error('Firebase not initialized')
  }

  // Verify caller is admin
  if (adminData.role !== 'admin' && adminData.role !== 'org_admin' && adminData.role !== 'super_admin') {
    throw new Error('Only administrators can create staff accounts')
  }

  // Store current admin user credentials temporarily
  const currentUser = auth.currentUser
  if (!currentUser) {
    throw new Error('Admin must be logged in to create users')
  }

  // Get secondary auth instance to create user without affecting main session
  const secondaryAuth = getSecondaryAuth()
  if (!secondaryAuth) {
    throw new Error('Failed to initialize secondary auth')
  }

  try {
    // Create the new user using secondary auth (doesn't affect main auth session)
    const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password)
    const newUser = userCredential.user

    // Update profile with display name
    await updateProfile(newUser, { displayName })

    // Sign out the new user from secondary auth immediately
    await secondaryAuth.signOut()

    // Create user document in Firestore
    const userData: UserData = {
      uid: newUser.uid,
      email: newUser.email || '',
      displayName: displayName,
      companyName: adminData.companyName,
      companyId: adminData.companyId || (adminData as any).businessId,
      role: role,
      status: 'active',
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString(),
      createdBy: adminData.uid
    }

    const userDocRef = doc(db, COLLECTIONS.USERS, newUser.uid)
    await setDoc(userDocRef, userData)

    // Clean up secondary auth app
    await cleanupSecondaryAuth()

    return userData
  } catch (error) {
    // Clean up secondary auth on error too
    await cleanupSecondaryAuth()

    console.error('Create staff user error:', error)
    if (error && typeof error === 'object' && 'code' in error) {
        throw new Error(getAuthErrorMessage((error as {code: string}).code))
    }
    throw new Error('An unknown error occurred while creating the staff user.');
  }
}

// Get all users for a company - Admin only
export const getCompanyUsers = async (companyId: string): Promise<UserData[]> => {
  if (!db) {
    throw new Error('Firebase not initialized')
  }

  try {
    const usersRef = collection(db, COLLECTIONS.USERS)
    const q = query(usersRef, where('companyId', '==', companyId))
    const querySnapshot = await getDocs(q)

    const users: UserData[] = []
    querySnapshot.forEach((doc) => {
      users.push(doc.data() as UserData)
    })

    // Sort by role: admin first, then manager, then cashier
    const roleOrder = { super_admin: 0, org_admin: 1, admin: 1, manager: 2, staff: 3, cashier: 3, customer: 4 }
    users.sort((a, b) => roleOrder[a.role] - roleOrder[b.role])

    return users
  } catch (error: unknown) {
    console.error('Get company users error:', error)
    throw new Error('Failed to fetch users')
  }
}

// Update user role - Admin only
export const updateUserRole = async (
  targetUid: string,
  newRole: 'manager' | 'staff',
  adminData: UserData
): Promise<void> => {
  if (!db) {
    throw new Error('Firebase not initialized')
  }

  // Verify caller is admin
  if (adminData.role !== 'admin' && adminData.role !== 'org_admin' && adminData.role !== 'super_admin') {
    throw new Error('Only administrators can change user roles')
  }

  // Prevent changing own role
  if (targetUid === adminData.uid) {
    throw new Error('Cannot change your own role')
  }

  try {
    const userDocRef = doc(db, COLLECTIONS.USERS, targetUid)
    const userDoc = await getDoc(userDocRef)

    if (!userDoc.exists()) {
      throw new Error('User not found')
    }

    const userData = userDoc.data() as UserData

    // Verify user belongs to same company
    if (userData.companyId !== adminData.companyId) {
      throw new Error('Cannot modify users from other companies')
    }

    // Cannot change another admin's role
    if (userData.role === 'admin' || userData.role === 'org_admin' || userData.role === 'super_admin') {
      throw new Error('Cannot change role of another administrator')
    }

    await updateDoc(userDocRef, { role: newRole })
  } catch (error: unknown) {
    console.error('Update user role error:', error)
    throw error
  }
}

// Update user status (activate/deactivate) - Admin only
export const updateUserStatus = async (
  targetUid: string,
  newStatus: 'active' | 'inactive',
  adminData: UserData
): Promise<void> => {
  if (!db) {
    throw new Error('Firebase not initialized')
  }

  // Verify caller is admin
  if (adminData.role !== 'admin' && adminData.role !== 'org_admin' && adminData.role !== 'super_admin') {
    throw new Error('Only administrators can change user status')
  }

  // Prevent deactivating own account
  if (targetUid === adminData.uid) {
    throw new Error('Cannot deactivate your own account')
  }

  try {
    const userDocRef = doc(db, COLLECTIONS.USERS, targetUid)
    const userDoc = await getDoc(userDocRef)

    if (!userDoc.exists()) {
      throw new Error('User not found')
    }

    const userData = userDoc.data() as UserData
    const isSuperAdmin = adminData.role === 'super_admin'

    if (userData.role === 'super_admin') {
      throw new Error('Cannot modify another super admin')
    }

    if (!isSuperAdmin) {
      // Verify user belongs to same company
      if (userData.companyId !== adminData.companyId) {
        throw new Error('Cannot modify users from other companies')
      }

      // Cannot deactivate another admin
      if (userData.role === 'admin' || userData.role === 'org_admin') {
        throw new Error('Cannot deactivate another administrator')
      }
    }

    await updateDoc(userDocRef, { status: newStatus })
  } catch (error: unknown) {
    console.error('Update user status error:', error)
    throw error
  }
}

// Delete a staff user - Admin only (removes from Firestore only, Firebase Auth deletion requires Admin SDK)
export const deleteStaffUser = async (
  targetUid: string,
  adminData: UserData
): Promise<void> => {
  if (!db) {
    throw new Error('Firebase not initialized')
  }

  // Verify caller is admin
  if (adminData.role !== 'admin' && adminData.role !== 'org_admin' && adminData.role !== 'super_admin') {
    throw new Error('Only administrators can delete users')
  }

  // Prevent deleting own account
  if (targetUid === adminData.uid) {
    throw new Error('Cannot delete your own account')
  }

  try {
    const userDocRef = doc(db, COLLECTIONS.USERS, targetUid)
    const userDoc = await getDoc(userDocRef)

    if (!userDoc.exists()) {
      throw new Error('User not found')
    }

    const userData = userDoc.data() as UserData
    const isSuperAdmin = adminData.role === 'super_admin'

    if (userData.role === 'super_admin') {
      throw new Error('Cannot delete another super admin')
    }

    if (!isSuperAdmin) {
      // Verify user belongs to same company
      if (userData.companyId !== adminData.companyId) {
        throw new Error('Cannot delete users from other companies')
      }

      // Cannot delete another admin
      if (userData.role === 'admin' || userData.role === 'org_admin') {
        throw new Error('Cannot delete another administrator')
      }
    }

    // Delete user document from Firestore
    await deleteDoc(userDocRef)

    // Note: This only removes the Firestore document.
    // The Firebase Auth account still exists but user won't have access
    // since their companyId data is gone. For full deletion, use Firebase Admin SDK.
  } catch (error: unknown) {
    console.error('Delete staff user error:', error)
    throw error
  }
}
